import React, { ChangeEvent, ChangeEventHandler, memo, useCallback, useMemo } from 'react';

import { observer } from 'mobx-react-lite';
import { action } from 'mobx';

import { Campaign } from 'models';

import Switch from '@vromo/ui/components/Switch';
import Input from '@vromo/ui/components/Input';
import Tooltip from '@vromo/ui/components/Tooltip';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import SMSTemplateEditor from './SMSTemplateEditor';
import styles, { ItemContainer } from './SMSEditor.style';
import { useTranslation } from 'react-i18next';
import { campaingLinkModeOptions } from './SMSEditorHelper';

interface Props {
    campaign: Campaign;
}

interface ToggleSwitchContainerProps {
    labelName: string;
    toolTip: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
}

const ToggleSwitchContainer = memo(
    ({ labelName, toolTip, checked, onChange }: ToggleSwitchContainerProps) => {
        return (
            <ItemContainer>
                <label>{labelName}</label>
                <Tooltip position="bottom" label={toolTip}>
                    <div style={styles.infoIconContainer}>
                        <FontAwesome icon="info-circle" style={styles.infoIcon} />
                    </div>
                </Tooltip>
                <Switch style={styles.enabledToggle} {...{ checked, onChange }} />
            </ItemContainer>
        );
    }
);

interface SelectSMSTriggerProps {
    labelName: string;
    campaignLinkMode: string;
    onChange: ChangeEventHandler;
    SMSTriggerOptions: any;
}

const SelectSMSTrigger = memo(
    ({ labelName, campaignLinkMode, onChange, SMSTriggerOptions }: SelectSMSTriggerProps) => {
        return (
            <ItemContainer>
                <label style={styles.sendWhenLabel}>{labelName}</label>
                <select value={campaignLinkMode} onChange={onChange}>
                    {Object.entries(SMSTriggerOptions).map(([key, value]) => (
                        <option value={key} key={key}>
                            {value}
                        </option>
                    ))}
                </select>
            </ItemContainer>
        );
    }
);

const SenderInput = memo((props) => <Input {...props} />);

const SMSEditor = observer<Props>(({ campaign }) => {
    const {
        enabled: campaignLinkEnabled,
        mode: campaignLinkMode,
        sms: campaignLinkSms,
        proxyEnabled: campaignLinkProxyEnabled,
        overflowEnabled: campaignOverflowEnabled
    } = campaign.link!;

    const { t } = useTranslation();

    const SMSTriggerOptions = useMemo(() => campaingLinkModeOptions(t), []);

    const onEnableToggled = useCallback(
        action((enabled: boolean) => (campaign.link!.enabled = enabled)),
        [campaign.link!.enabled]
    );

    const onMaskingToggled = useCallback(
        action((enabled: boolean) => (campaign.link!.proxyEnabled = enabled)),
        [campaign.link!.proxyEnabled]
    );

    const onOverflowToggled = useCallback(
        action((enabled: boolean) => (campaign.link!.overflowEnabled = enabled)),
        [campaign.link!.overflowEnabled]
    );

    const onSelectTrigger = useCallback(
        action((e: ChangeEvent<HTMLSelectElement>) => (campaign.link!.mode = e.target.value)),
        [campaign.link!.mode]
    );

    const onChangeTemplate = useCallback(
        action((sms: string) => (campaign.link!.sms = sms)),
        [campaign.link!.sms]
    );

    return (
        <div>
            <ToggleSwitchContainer
                labelName={t('campaign.enabled')}
                toolTip={t('campaign.enableToggleTooltip')}
                checked={campaignLinkEnabled}
                onChange={onEnableToggled}
            />
            <SelectSMSTrigger
                {...{ campaignLinkMode, SMSTriggerOptions }}
                labelName={t('campaign.sendWhen')}
                onChange={onSelectTrigger}
            />
            <ItemContainer>
                <SMSTemplateEditor value={campaignLinkSms} onChange={onChangeTemplate} />
            </ItemContainer>
            <ToggleSwitchContainer
                labelName={t('campaign.sendForOverflow')}
                toolTip={t('campaign.sendForOverflowTooltip')}
                onChange={onOverflowToggled}
                checked={campaignOverflowEnabled}
            />
            <hr />
            <ToggleSwitchContainer
                labelName={t('campaign.phoneMasking')}
                toolTip={t('campaign.phoneLabel')}
                onChange={onMaskingToggled}
                checked={campaignLinkProxyEnabled}
            />
        </div>
    );
});

export default SMSEditor;
